/**
 * Hook for the Tilannekatsaus (status overview) page.
 *
 * Fetches the tasks for a single project filtered by an exact task title
 * (one of the 9 canonical template titles). Skips entirely until both a
 * project and a title are selected.
 */

import { useQuery, gql } from "@apollo/client";
import type { TaskStatus, SubtaskProgress } from "@/types";

export interface OverviewTask {
  id: string;
  title: string;
  status: TaskStatus;
  updatedAt: string;
  dueDate: string | null;
  // Carried so TaskDialog's sync-on-close guard still resolves after Apollo
  // clears the detail query (skip → data undefined) when the dialog closes.
  taskType: string;
  externalInspectionPlatformInspectionId: string | null;
  subtaskProgress?: SubtaskProgress | null;
  procurementPackage?: {
    code: string | null;
    name: string | null;
  } | null;
  section?: {
    id: string;
    code: string | null;
    name: string | null;
  } | null;
  responsibleUser?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    substituteUserId: string | null;
    substituteFirstName: string | null;
    substituteLastName: string | null;
  } | null;
  location?: {
    id: string;
    name: string | null;
    hierarchyPath: string[] | null;
  } | null;
  plot?: {
    id: string;
    name: string | null;
  } | null;
}

export const GET_OVERVIEW_TASKS = gql`
  query GetOverviewTasks($limit: Int, $filter: TaskFilterInput) {
    tasks(limit: $limit, filter: $filter) {
      items {
        id
        title
        status
        updatedAt
        dueDate
        taskType
        externalInspectionPlatformInspectionId
        subtaskProgress {
          pending
          inProgress
          completed
          problem
          total
        }
        procurementPackage {
          code
          name
        }
        section {
          id
          code
          name
        }
        responsibleUser {
          id
          firstName
          lastName
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        location {
          id
          name
          hierarchyPath
        }
        plot {
          id
          name
        }
      }
    }
  }
`;

interface OverviewTasksQueryResult {
  tasks: {
    items: OverviewTask[];
  };
}

export function useOverviewTasks(
  projectId: string | null,
  title: string | null,
) {
  const { data, loading, error, refetch } = useQuery<OverviewTasksQueryResult>(
    GET_OVERVIEW_TASKS,
    {
      variables: {
        // One hanke can hold ~1000 tasks of a single title; 500 silently hid
        // rows in Tilannekatsaus. The backend supports cursor pagination —
        // move to it when a hanke approaches this number.
        limit: 2000,
        filter: { projectId, titles: title ? [title] : undefined },
      },
      skip: !projectId || !title,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    tasks: (data?.tasks.items ?? []) as OverviewTask[],
    loading,
    error,
    refetch,
  };
}
